/**
 * pushStore.ts - 알림함(종 아이콘) 상태
 *
 * 서버(sys_alarm_rcv)가 기준이다. FCM 수신은 "새 알림이 왔다"는 신호로만 쓰고,
 * 실제 목록/미확인 수는 매번 백엔드 /alarm/list(FcmAlarmMapper.getRecentFcmAlarm)를 다시 불러 맞춘다.
 *  - refresh(): 로그인 직후(FcmNotificationBell 마운트), FCM 수신(포그라운드 onMessage / 백그라운드 SW postMessage) 시 호출
 *  - readAll(): 미확인 중 tm_reg가 가장 최신인 no_message를 /alarm/read-all 로 보내 일괄 읽음처리 후 refresh
 *  - markRead(): 한 건 읽음처리(/alarm/read) 후 refresh
 * Header 종 아이콘은 unreadCount > 0 이면 빨간 점을 표시한다.
 */
import { create } from "zustand";
import { apiFetch } from "@shared/api/api";

/** FcmAlarmMapper.getRecentFcmAlarm 결과 한 건 */
export type AlarmItem = {
  no_message: string;
  nm_menu?: string | null;
  no_ref?: string | null;
  tm_reg: string | number; // "yyyy-MM-dd HH:mm:ss.SSS" 또는 epoch(ms) - 직접 selectList 라 Jackson 직렬화 그대로
  dc_message: string;
  yn_read: "Y" | "N";
  cd_menu?: string | null;
};

/** tm_reg("yyyy-MM-dd HH:mm:ss(.SSS)" 또는 epoch ms) → epoch ms. 파싱 실패 시 0 */
function tmRegToMillis(tm: string | number | null | undefined): number {
  if (tm === null || tm === undefined || tm === "") return 0;
  const t =
    typeof tm === "number"
      ? tm
      : new Date(String(tm).replace(" ", "T").substring(0, 19)).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** 목록 중 tm_reg가 가장 최신인 행의 no_message. no_message는 uuid라 크기 비교가 안 되므로 tm_reg로 고른다 */
export function latestNoMessage(
  rows: { no_message: string; tm_reg: string | number | null | undefined }[]
): string | null {
  let latest: { no_message: string; t: number } | null = null;
  for (const row of rows) {
    const t = tmRegToMillis(row.tm_reg);
    if (!latest || t > latest.t) latest = { no_message: String(row.no_message), t };
  }
  return latest ? latest.no_message : null;
}

type PushState = {
  items: AlarmItem[];
  unreadCount: number;
  loading: boolean;
  refresh: () => Promise<void>;
  readAll: () => Promise<void>;
  markRead: (no_message: string) => Promise<void>;
};

let refreshing: Promise<void> | null = null;

export const usePushStore = create<PushState>((set, get) => ({
  items: [],
  unreadCount: 0,
  loading: false,

  refresh: async () => {
    // 짧은 시간에 여러 건이 연달아 오면 한 번만 조회한다
    if (refreshing) return refreshing;
    refreshing = (async () => {
      set({ loading: true });
      try {
        const res = await apiFetch<AlarmItem[]>("/alarm/list", {
          method: "POST",
        });
        const items = Array.isArray(res?.data) ? res.data : [];
        set({
          items,
          unreadCount: items.filter((i) => i.yn_read === "N").length,
        });
      } catch (e) {
        console.debug("[ALARM] 목록 조회 실패", e);
      } finally {
        set({ loading: false });
        refreshing = null;
      }
    })();
    return refreshing;
  },

  readAll: async () => {
    const unread = get().items.filter((i) => i.yn_read === "N");
    if (unread.length === 0) return;
    const latest = latestNoMessage(unread);
    if (!latest) return;
    try {
      await apiFetch("/alarm/read-all", {
        method: "POST",
        body: { no_message: latest } as any,
      });
    } catch (e) {
      console.debug("[ALARM] 모두읽음 실패", e);
    }
    await get().refresh();
  },

  markRead: async (no_message) => {
    try {
      await apiFetch("/alarm/read", {
        method: "POST",
        body: { no_message } as any,
      });
    } catch (e) {
      console.debug("[ALARM] 읽음처리 실패", e);
    }
    await get().refresh();
  },
}));
